import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Trip, Destination, SubDestination, TodoItem, Accommodation, Transportation } from '../types';
import { createLogger } from '../utils/logger';

const logger = createLogger('store');

// Extended interfaces for store-specific fields
interface ExtendedDestination extends Destination {
  description?: string;
  media?: any[];
}

interface ExtendedSubDestination extends SubDestination {
  duration: string;
}

interface ExtendedAccommodation extends Accommodation {
  phoneNumber?: string;
}

interface ExtendedTransportation extends Transportation {
  // All fields are now in the base type
}

interface ExtendedTrip extends Trip {
  destinations: ExtendedDestination[];
  accommodations?: ExtendedAccommodation[];
  transportation?: ExtendedTransportation[];
  media?: any[];
  claimToken?: string;
}

// UI State slice
interface UIState {
  // Modals
  showAddModal: boolean;
  showAiModal: boolean;
  showMergeDialog: boolean;
  showAccommodationModal: boolean;
  showTransportModal: boolean;
  showTransportMethodModal: boolean;
  showTransportAiModal: boolean;
  showAccommodationMethodModal: boolean;
  showAccommodationAiModal: boolean;
  showShareModal: boolean;
  
  // Active items
  activeTab: 'timeline' | 'overview' | 'hotels' | 'transport' | 'todos' | 'split';
  expandedDestinations: Set<string>;
  editingDuration: string | null;
  addingTo: { type: 'destination' | 'subdestination' | 'todo', parentId?: string } | null;
  
  // Editing items
  editingAccommodation: Accommodation | null;
  editingTransport: Transportation | null;
  
  // Deletion items
  accommodationToDelete: Accommodation | null;
  transportToDelete: Transportation | null;
  destinationToDelete: Destination | null;
  stopToDelete: { stop: SubDestination, parentId: string } | null;
  todoToDelete: { todo: TodoItem, parentId: string } | null;
  
  // Form state
  newItemName: string;
  aiSuggestionText: string;
  transportAiText: string;
  accommodationAiText: string;
  
  // Loading states
  isSaving: boolean;
  readOnlyMode: boolean;
  
  // Toast state
  toast: {
    message: string;
    type: 'error' | 'success' | 'info';
    isVisible: boolean;
  } | null;
  
  // Actions
  setShowAddModal: (show: boolean) => void;
  setReadOnlyMode: (readOnly: boolean) => void;
  showToast: (message: string, type: 'error' | 'success' | 'info') => void;
  hideToast: () => void;
  setShowAiModal: (show: boolean) => void;
  setShowMergeDialog: (show: boolean) => void;
  setShowAccommodationModal: (show: boolean) => void;
  setShowTransportModal: (show: boolean) => void;
  setShowTransportMethodModal: (show: boolean) => void;
  setShowTransportAiModal: (show: boolean) => void;
  setShowAccommodationMethodModal: (show: boolean) => void;
  setShowAccommodationAiModal: (show: boolean) => void;
  setShowShareModal: (show: boolean) => void;
  setActiveTab: (tab: 'timeline' | 'overview' | 'hotels' | 'transport' | 'todos' | 'split') => void;
  toggleDestinationExpanded: (id: string) => void;
  setEditingDuration: (id: string | null) => void;
  setAddingTo: (value: { type: 'destination' | 'subdestination' | 'todo', parentId?: string } | null) => void;
  setEditingAccommodation: (accommodation: ExtendedAccommodation | null) => void;
  setEditingTransport: (transport: ExtendedTransportation | null) => void;
  setAccommodationToDelete: (accommodation: ExtendedAccommodation | null) => void;
  setTransportToDelete: (transport: ExtendedTransportation | null) => void;
  setDestinationToDelete: (destination: ExtendedDestination | null) => void;
  setStopToDelete: (value: { stop: SubDestination, parentId: string } | null) => void;
  setTodoToDelete: (value: { todo: TodoItem, parentId: string } | null) => void;
  setNewItemName: (name: string) => void;
  setAiSuggestionText: (text: string) => void;
  setTransportAiText: (text: string) => void;
  setAccommodationAiText: (text: string) => void;
  setIsSaving: (saving: boolean) => void;
  resetModals: () => void;
}

// AI State slice
interface AIState {
  aiJobId: string | null;
  aiJobStartTime: number | null;
  aiSuggestion: any | null;
  isAiRefinement: boolean;
  previousSuggestionExpired: boolean;
  transportAiJobId: string | null;
  transportAiJobStartTime: number | null;
  pendingTransport: Transportation | null;
  accommodationAiJobId: string | null;
  accommodationAiJobStartTime: number | null;
  
  // AI Suggestions confirmation
  pendingTransportations: Transportation[] | null;
  showTransportConfirmDialog: boolean;
  pendingAccommodations: Accommodation[] | null;
  showAccommodationConfirmDialog: boolean;
  
  // Expense parsing
  expenseParseJobId: string | null;
  expenseParseStartTime: number | null;
  
  // Actions
  setAiJobId: (id: string | null) => void;
  setAiSuggestion: (suggestion: any | null) => void;
  setIsAiRefinement: (isRefinement: boolean) => void;
  setPreviousSuggestionExpired: (expired: boolean) => void;
  setTransportAiJobId: (id: string | null) => void;
  setPendingTransport: (transport: ExtendedTransportation | null) => void;
  setAccommodationAiJobId: (id: string | null) => void;
  setPendingTransportations: (transportations: Transportation[] | null) => void;
  setShowTransportConfirmDialog: (show: boolean) => void;
  setPendingAccommodations: (accommodations: Accommodation[] | null) => void;
  setShowAccommodationConfirmDialog: (show: boolean) => void;
  setExpenseParseJobId: (id: string | null) => void;
  setExpenseParseStartTime: (time: number | null) => void;
  resetAiState: () => void;
}

// Trip State slice
interface TripState {
  trip: ExtendedTrip | null;
  hasParsedDataApplied: boolean;
  
  // Actions
  setTrip: (trip: ExtendedTrip | null) => void;
  setHasParsedDataApplied: (applied: boolean) => void;
  updateTrip: (updater: (trip: ExtendedTrip) => ExtendedTrip) => void;
}

// Combined store
interface AppStore extends UIState, AIState, TripState {}

const useStore = create<AppStore>()(
  devtools(
    (set) => ({
      // UI State
      showAddModal: false,
      showAiModal: false,
      showMergeDialog: false,
      showAccommodationModal: false,
      showTransportModal: false,
      showTransportMethodModal: false,
      showTransportAiModal: false,
      showAccommodationMethodModal: false,
      showAccommodationAiModal: false,
      showShareModal: false,
      activeTab: 'timeline',
      expandedDestinations: new Set(),
      editingDuration: null,
      addingTo: null,
      editingAccommodation: null,
      editingTransport: null,
      accommodationToDelete: null,
      transportToDelete: null,
      destinationToDelete: null,
      stopToDelete: null,
      todoToDelete: null,
      newItemName: '',
      aiSuggestionText: '',
      transportAiText: '',
      accommodationAiText: '',
      isSaving: false,
      readOnlyMode: false,
      toast: null,
      
      // UI Actions
      setShowAddModal: (show) => set({ showAddModal: show }),
      setReadOnlyMode: (readOnly) => set({ readOnlyMode: readOnly }),
      showToast: (message, type) => set({ 
        toast: { message, type, isVisible: true } 
      }),
      hideToast: () => set({ toast: null }),
      setShowAiModal: (show) => set({ showAiModal: show }),
      setShowMergeDialog: (show) => {
        logger.debug('setShowMergeDialog called with:', show);
        set({ showMergeDialog: show });
      },
      setShowAccommodationModal: (show) => set({ showAccommodationModal: show }),
      setShowTransportModal: (show) => set({ showTransportModal: show }),
      setShowTransportMethodModal: (show) => set({ showTransportMethodModal: show }),
      setShowTransportAiModal: (show) => set({ showTransportAiModal: show }),
      setShowAccommodationMethodModal: (show) => set({ showAccommodationMethodModal: show }),
      setShowAccommodationAiModal: (show) => set({ showAccommodationAiModal: show }),
      setShowShareModal: (show) => set({ showShareModal: show }),
      setActiveTab: (tab) => set({ activeTab: tab }),
      toggleDestinationExpanded: (id) => set((state) => {
        const newSet = new Set(state.expandedDestinations);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return { expandedDestinations: newSet };
      }),
      setEditingDuration: (id) => set({ editingDuration: id }),
      setAddingTo: (value) => set({ addingTo: value }),
      setEditingAccommodation: (accommodation) => set({ editingAccommodation: accommodation }),
      setEditingTransport: (transport) => set({ editingTransport: transport }),
      setAccommodationToDelete: (accommodation) => set({ accommodationToDelete: accommodation }),
      setTransportToDelete: (transport) => set({ transportToDelete: transport }),
      setDestinationToDelete: (destination) => set({ destinationToDelete: destination }),
      setStopToDelete: (value) => set({ stopToDelete: value }),
      setTodoToDelete: (value) => set({ todoToDelete: value }),
      setNewItemName: (name) => set({ newItemName: name }),
      setAiSuggestionText: (text) => set({ aiSuggestionText: text }),
      setTransportAiText: (text) => set({ transportAiText: text }),
      setAccommodationAiText: (text) => set({ accommodationAiText: text }),
      setIsSaving: (saving) => set({ isSaving: saving }),
      resetModals: () => set({
        showAddModal: false,
        showAiModal: false,
        showMergeDialog: false,
        showAccommodationModal: false,
        showTransportModal: false,
        showTransportMethodModal: false,
        showTransportAiModal: false,
        showAccommodationMethodModal: false,
        showAccommodationAiModal: false,
        showShareModal: false,
        newItemName: '',
        aiSuggestionText: '',
        transportAiText: '',
        accommodationAiText: '',
      }),
      
      // AI State
      aiJobId: null,
      aiJobStartTime: null,
      aiSuggestion: null,
      isAiRefinement: false,
      previousSuggestionExpired: false,
      transportAiJobId: null,
      transportAiJobStartTime: null,
      pendingTransport: null,
      accommodationAiJobId: null,
      accommodationAiJobStartTime: null,
      pendingTransportations: null,
      showTransportConfirmDialog: false,
      pendingAccommodations: null,
      showAccommodationConfirmDialog: false,
      expenseParseJobId: null,
      expenseParseStartTime: null,
      
      // AI Actions
      setAiJobId: (id) => set({ 
        aiJobId: id,
        aiJobStartTime: id ? Date.now() : null
      }),
      setAiSuggestion: (suggestion) => {
        logger.debug('setAiSuggestion called with:', suggestion);
        set({ aiSuggestion: suggestion });
      },
      setIsAiRefinement: (isRefinement) => set({ isAiRefinement: isRefinement }),
      setPreviousSuggestionExpired: (expired) => set({ previousSuggestionExpired: expired }),
      setTransportAiJobId: (id) => set({ 
        transportAiJobId: id,
        transportAiJobStartTime: id ? Date.now() : null
      }),
      setPendingTransport: (transport) => set({ pendingTransport: transport }),
      setAccommodationAiJobId: (id) => set({ 
        accommodationAiJobId: id,
        accommodationAiJobStartTime: id ? Date.now() : null
      }),
      setPendingTransportations: (transportations) => set({ pendingTransportations: transportations }),
      setShowTransportConfirmDialog: (show) => set({ showTransportConfirmDialog: show }),
      setPendingAccommodations: (accommodations) => set({ pendingAccommodations: accommodations }),
      setShowAccommodationConfirmDialog: (show) => set({ showAccommodationConfirmDialog: show }),
      setExpenseParseJobId: (id) => set({ expenseParseJobId: id }),
      setExpenseParseStartTime: (time) => set({ expenseParseStartTime: time }),
      resetAiState: () => set({
        aiJobId: null,
        aiJobStartTime: null,
        aiSuggestion: null,
        isAiRefinement: false,
        previousSuggestionExpired: false,
        transportAiJobId: null,
        transportAiJobStartTime: null,
        pendingTransport: null,
        accommodationAiJobId: null,
        accommodationAiJobStartTime: null,
        pendingTransportations: null,
        showTransportConfirmDialog: false,
        pendingAccommodations: null,
        showAccommodationConfirmDialog: false,
        expenseParseJobId: null,
        expenseParseStartTime: null,
      }),
      
      // Trip State
      trip: null,
      hasParsedDataApplied: false,
      
      // Trip Actions
      setTrip: (trip) => set({ trip }),
      setHasParsedDataApplied: (applied) => set({ hasParsedDataApplied: applied }),
      updateTrip: (updater) => set((state) => ({
        trip: state.trip ? updater(state.trip) : null
      })),
    }),
    {
      name: 'route-for-you-store',
    }
  )
);

export default useStore;

// Selectors for common combinations
export const useUIState = () => useStore((state) => ({
  showAddModal: state.showAddModal,
  showAiModal: state.showAiModal,
  showMergeDialog: state.showMergeDialog,
  showAccommodationModal: state.showAccommodationModal,
  showTransportModal: state.showTransportModal,
  showTransportMethodModal: state.showTransportMethodModal,
  showTransportAiModal: state.showTransportAiModal,
  showAccommodationMethodModal: state.showAccommodationMethodModal,
  showAccommodationAiModal: state.showAccommodationAiModal,
  activeTab: state.activeTab,
  expandedDestinations: state.expandedDestinations,
  editingDuration: state.editingDuration,
  addingTo: state.addingTo,
  editingAccommodation: state.editingAccommodation,
  editingTransport: state.editingTransport,
  newItemName: state.newItemName,
  aiSuggestionText: state.aiSuggestionText,
  transportAiText: state.transportAiText,
  accommodationAiText: state.accommodationAiText,
  isSaving: state.isSaving,
}));

export const useAIState = () => useStore((state) => ({
  aiJobId: state.aiJobId,
  aiJobStartTime: state.aiJobStartTime,
  aiSuggestion: state.aiSuggestion,
  isAiRefinement: state.isAiRefinement,
  previousSuggestionExpired: state.previousSuggestionExpired,
  transportAiJobId: state.transportAiJobId,
  transportAiJobStartTime: state.transportAiJobStartTime,
  pendingTransport: state.pendingTransport,
  accommodationAiJobId: state.accommodationAiJobId,
  accommodationAiJobStartTime: state.accommodationAiJobStartTime,
}));

export const useTripState = () => useStore((state) => ({
  trip: state.trip,
  hasParsedDataApplied: state.hasParsedDataApplied,
}));